import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../../api/apiFetch";

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export type TradeMatchingFileType = "dtcc" | "regis";

export type TradeMatchingJobKind = "dtcc" | "emirremit";

export const jobKindForFileType = (
  fileType: TradeMatchingFileType,
): TradeMatchingJobKind => (fileType === "dtcc" ? "dtcc" : "emirremit");

type UploadTradeMatchingFilePayload = {
  file: File;
  fileType: TradeMatchingFileType;
};

export type DagsterRunStatus =
  | "QUEUED"
  | "STARTING"
  | "STARTED"
  | "SUCCESS"
  | "FAILURE"
  | "CANCELING"
  | "CANCELED"
  | "NOT_STARTED"
  | "MANAGED";

export type DagsterRunSummary = {
  runId: string;
  status: DagsterRunStatus | string;
  startTime: string | null;
  endTime: string | null;
  blobPath: string | null;
  date: string | null;
  runUrl: string | null;
};

export type TradeMatchCount = {
  total: number;
  matched: number;
};

export type TradeMatchingRow = {
  uti: string;
  isin: string | null;
  dateTrade: string | null;
  datetimeTrade: string | null;
  currency: string | null;
  mQuantity: number | null;
  mQuantityEmirRaw: number | null;
  mPriceLcy: number | null;
  mNotionalAmountLcy: string | null;
  tradeDirection: string;
  tradeMatch: number;
  source: string | null;
};

export type TradeMatchingFileInfo = {
  fileName: string;
  fileType: TradeMatchingFileType;
  uploadDate: string;
  sizeBytes: number;
  lastModified: string;
  uploader: string | null;
  lastRun: DagsterRunSummary | null;
  matchCount: TradeMatchCount | null;
};

type ListTradeMatchingFilesResponse = {
  files: TradeMatchingFileInfo[];
};

type GetTradeMatchingRowsResponse = {
  source: string;
  rows: TradeMatchingRow[];
};

export const isInFlightStatus = (status: string): boolean =>
  status === "QUEUED" || status === "STARTING" || status === "STARTED";

export const useUploadTradeMatchingFile = (options?: MutationCallbacks) => {
  return useMutation({
    mutationFn: async ({ file, fileType }: UploadTradeMatchingFilePayload) => {
      const response = await apiFetch("UploadTradeMatchingFile", {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "X-File-Name": file.name,
          "X-File-Type": fileType,
          "Content-Disposition": `attachment; filename="${file.name}"`,
        },
        body: file,
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Upload failed");
      }

      return response.text();
    },
    onSuccess: () => options?.onSuccess?.(),
    onError: (error: Error) => options?.onError?.(error),
  });
};

export type TriggerTradeMatchingRunResult = {
  runId: string;
  runUrl: string | null;
};

export type TriggerTradeMatchingRunPayload = {
  jobKind: TradeMatchingJobKind;
  blobPath: string;
};

export const useTriggerTradeMatchingRun = (options?: {
  onSuccess?: (result: TriggerTradeMatchingRunResult) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: TriggerTradeMatchingRunPayload,
    ): Promise<TriggerTradeMatchingRunResult> => {
      const response = await apiFetch("TriggerTradeMatchingRun", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobKind: payload.jobKind,
          blobPath: payload.blobPath,
        }),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Failed to trigger run");
      }

      return (await response.json()) as TriggerTradeMatchingRunResult;
    },
    onSuccess: (result, variables) => {
      const optimisticRun: DagsterRunSummary = {
        runId: result.runId,
        status: "STARTED",
        startTime: new Date().toISOString(),
        endTime: null,
        blobPath: variables.blobPath,
        date: null,
        runUrl: result.runUrl,
      };

      queryClient.setQueriesData<ListTradeMatchingFilesResponse>(
        { queryKey: ["ListTradeMatchingFiles"] },
        (old) => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            files: old.files.map((f) => {
              const filePath = `trade-matching/${f.fileType}/${f.uploadDate}/${f.fileName}`;
              return variables.blobPath === filePath
                ? { ...f, lastRun: optimisticRun }
                : f;
            }),
          };
        },
      );

      options?.onSuccess?.(result);
    },
    onError: (error: Error) => options?.onError?.(error),
  });
};

export const useTradeMatchingRows = (source: string | null) => {
  return useQuery<GetTradeMatchingRowsResponse, Error, TradeMatchingRow[]>({
    queryKey: ["GetTradeMatchingRows", { source }],
    enabled: source !== null && source.length > 0,
    select: (data) => data.rows,
  });
};

export const useListTradeMatchingFiles = (
  from: string | null,
  to: string | null,
) => {
  return useQuery<
    ListTradeMatchingFilesResponse,
    Error,
    TradeMatchingFileInfo[]
  >({
    queryKey: ["ListTradeMatchingFiles", { from, to }],
    enabled: from !== null && to !== null,
    staleTime: Infinity,
    select: (data) => data.files,
    refetchInterval: (query) => {
      const data = query.state.data as
        | ListTradeMatchingFilesResponse
        | undefined;
      const inFlightRuns = data?.files
        .map((f) => f.lastRun)
        .filter(
          (r): r is DagsterRunSummary =>
            r !== null && isInFlightStatus(r.status),
        );

      if (!inFlightRuns || inFlightRuns.length === 0) {
        return 60000;
      }

      const now = Date.now();
      const oldestStartMs = Math.min(
        ...inFlightRuns.map((r) =>
          r.startTime ? new Date(r.startTime).getTime() : now,
        ),
      );
      const sinceStart = now - oldestStartMs;

      // First refetch happens 15s after the run started so Dagster has time to
      // index it. After that, poll every 5s while runs are still in-flight.
      if (sinceStart < 15000) {
        return Math.max(15000 - sinceStart, 1000);
      }
      return 5000;
    },
  });
};
